import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Scale, Equal } from 'lucide-react';
import { formatDateFR } from './DateRangeFilter';

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

/**
 * Tableau Bilan bilatéral — style Compte de Résultat.
 * Colonne PASSIF à GAUCHE, colonne ACTIF à DROITE.
 * Groupement par catégorie (en-têtes de classe). TOTAL + ligne Résultat d'équilibrage.
 *
 * Props :
 *  - items : entrées bilan filtrées
 *  - periodLabel : libellé optionnel à afficher dans l'en-tête (ex. "01/01/2026 → 31/03/2026")
 */
export default function BilanBilateralTable({ items = [], periodLabel = '' }) {
  const actifs = items.filter((i) => i.type === 'actif');
  const passifs = items.filter((i) => i.type === 'passif');

  const groupByCategorie = (arr) => {
    const map = {};
    for (const it of arr) {
      const k = it.categorie || 'Divers';
      if (!map[k]) map[k] = [];
      map[k].push(it);
    }
    return Object.entries(map).map(([categorie, entries]) => ({
      categorie,
      entries,
      total: entries.reduce((s, e) => s + (parseFloat(e.montant) || 0), 0),
    }));
  };

  const actifsGrouped = groupByCategorie(actifs);
  const passifsGrouped = groupByCategorie(passifs);
  const totalActif = actifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);
  const totalPassif = passifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);
  const ecart = totalActif - totalPassif;
  const equilibre = Math.abs(ecart) < 0.01;
  const isBenefice = ecart > 0;

  return (
    <Card className="shadow-lg border-2 border-indigo-100" data-testid="bilan-bilateral">
      <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Scale className="h-6 w-6" />
            Bilan{periodLabel ? '' : ` - ${new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`}
          </span>
          {periodLabel && (
            <span className="text-sm font-normal opacity-95" data-testid="bilan-period-label">
              Période : {periodLabel}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* ACTIF À GAUCHE */}
          <div className="border-r border-gray-200">
            <div className="bg-blue-600 text-white px-6 py-3">
              <h3 className="font-bold text-lg uppercase tracking-wide">Actif</h3>
            </div>
            <table className="w-full">
              <thead className="bg-blue-50 border-b border-blue-200">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-blue-800 uppercase">Libellé</th>
                  <th className="px-6 py-2 text-right text-xs font-semibold text-blue-800 uppercase">Montant (FCFA)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {actifsGrouped.length === 0 ? (
                  <tr>
                    <td className="px-6 py-3 text-gray-400 italic">Aucun actif enregistré</td>
                    <td className="px-6 py-3 text-right text-gray-400">0</td>
                  </tr>
                ) : (
                  actifsGrouped.map((group) => (
                    <React.Fragment key={`act-${group.categorie}`}>
                      <tr className="bg-blue-100/70">
                        <td colSpan={2} className="px-4 py-2 text-xs font-bold text-blue-900 uppercase tracking-wide">
                          {group.categorie}
                        </td>
                      </tr>
                      {group.entries.map((e, i) => (
                        <tr key={`act-${group.categorie}-${i}`} className="hover:bg-blue-50/50">
                          <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                            {e.date && <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.date)}</span>}
                            {e.libelle || 'Divers'}
                          </td>
                          <td className="px-6 py-2 text-right text-blue-700 font-medium">{fmt(e.montant)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                )}
                {/* Équilibrage côté actif si perte (Actif < Passif) */}
                {!equilibre && !isBenefice && (
                  <tr className="bg-orange-50 border-t border-orange-200">
                    <td className="px-6 py-3 italic text-orange-700 font-medium">Résultat (Perte)</td>
                    <td className="px-6 py-3 text-right text-orange-700 font-bold">{fmt(Math.abs(ecart))}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PASSIF À DROITE */}
          <div>
            <div className="bg-amber-600 text-white px-6 py-3">
              <h3 className="font-bold text-lg uppercase tracking-wide">Passif</h3>
            </div>
            <table className="w-full">
              <thead className="bg-amber-50 border-b border-amber-200">
                <tr>
                  <th className="px-6 py-2 text-left text-xs font-semibold text-amber-800 uppercase">Libellé</th>
                  <th className="px-6 py-2 text-right text-xs font-semibold text-amber-800 uppercase">Montant (FCFA)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {passifsGrouped.length === 0 ? (
                  <tr>
                    <td className="px-6 py-3 text-gray-400 italic">Aucun passif enregistré</td>
                    <td className="px-6 py-3 text-right text-gray-400">0</td>
                  </tr>
                ) : (
                  passifsGrouped.map((group) => (
                    <React.Fragment key={`pas-${group.categorie}`}>
                      <tr className="bg-amber-100/70">
                        <td colSpan={2} className="px-4 py-2 text-xs font-bold text-amber-900 uppercase tracking-wide">
                          {group.categorie}
                        </td>
                      </tr>
                      {group.entries.map((e, i) => (
                        <tr key={`pas-${group.categorie}-${i}`} className="hover:bg-amber-50/50">
                          <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                            {e.date && <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.date)}</span>}
                            {e.libelle || 'Divers'}
                          </td>
                          <td className="px-6 py-2 text-right text-amber-700 font-medium">{fmt(e.montant)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                )}
                {/* Équilibrage côté passif si bénéfice (Actif > Passif) */}
                {!equilibre && isBenefice && (
                  <tr className="bg-blue-50 border-t border-blue-200">
                    <td className="px-6 py-3 italic text-blue-700 font-medium">Résultat (Bénéfice)</td>
                    <td className="px-6 py-3 text-right text-blue-700 font-bold">{fmt(ecart)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ligne TOTAL */}
        <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-r from-blue-100 via-gray-100 to-amber-100 border-t-2 border-b-2 border-gray-300 font-bold">
          <div className="px-6 py-4 flex items-center justify-between border-r border-gray-300">
            <span className="text-blue-900 uppercase tracking-wide">Total Actif</span>
            <span className="text-blue-900 text-xl" data-testid="bilateral-total-actif">
              {fmt(totalActif + (!equilibre && !isBenefice ? Math.abs(ecart) : 0))} FCFA
            </span>
          </div>
          <div className="px-6 py-4 flex items-center justify-between">
            <span className="text-amber-900 uppercase tracking-wide">Total Passif</span>
            <span className="text-amber-900 text-xl" data-testid="bilateral-total-passif">
              {fmt(totalPassif + (!equilibre && isBenefice ? ecart : 0))} FCFA
            </span>
          </div>
        </div>

        {/* Bandeau équilibre */}
        <div
          className={`px-6 py-4 border-t-4 ${
            equilibre
              ? 'bg-green-100 border-green-400'
              : isBenefice
              ? 'bg-blue-100 border-blue-400'
              : 'bg-orange-100 border-orange-400'
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Equal className="h-5 w-5" />
              <span className="font-bold text-lg uppercase tracking-wide">
                {equilibre ? 'Bilan Équilibré' : isBenefice ? 'Résultat (Bénéfice)' : 'Résultat (Perte)'}
              </span>
            </div>
            <span
              className={`font-bold text-2xl ${
                equilibre ? 'text-green-800' : isBenefice ? 'text-blue-800' : 'text-orange-800'
              }`}
              data-testid="bilateral-ecart"
            >
              {equilibre ? '0' : (isBenefice ? '+' : '-') + fmt(Math.abs(ecart))} FCFA
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
